/**
 * Cache service for storing non-user-specific data
 * Uses localStorage with 24-hour expiration
 */

interface CachedData<T> {
  data: T;
  timestamp: number;
  expiresAt: number;
}

const DEFAULT_CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
const MAX_CACHE_ENTRY_BYTES = 4 * 1024 * 1024; // 4MB safety margin under ~5MB origin limit

// Common TTL presets so endpoints share a consistent vocabulary.
export const CACHE_TTL = {
  FIVE_MINUTES: 5 * 60 * 1000,
  TEN_MINUTES: 10 * 60 * 1000,
  FIFTEEN_MINUTES: 15 * 60 * 1000,
  THIRTY_MINUTES: 30 * 60 * 1000,
  ONE_HOUR: 60 * 60 * 1000,
  ONE_DAY: 24 * 60 * 60 * 1000
} as const;

/**
 * Get cached data if it exists and is not expired
 */
export function getCachedData<T>(key: string): T | null {
  try {
    const cached = localStorage.getItem(key);
    if (!cached) {
      return null;
    }

    const cachedData: CachedData<T> = JSON.parse(cached);
    const now = Date.now();

    // Check if cache is expired
    if (now > cachedData.expiresAt) {
      // Remove expired cache
      localStorage.removeItem(key);
      return null;
    }

    return cachedData.data;
  } catch (error) {
    console.error('Error reading cache:', error);
    // If there's an error reading cache, remove it
    localStorage.removeItem(key);
    return null;
  }
}

/**
 * Store data in cache with an optional TTL (defaults to 24h for back-compat).
 * On quota-exceeded, wipes all cache_* entries and retries once so the
 * cache self-heals instead of silently staying stale forever.
 */
export function setCachedData<T>(key: string, data: T, ttlMs: number = DEFAULT_CACHE_DURATION): void {
  const now = Date.now();
  const cachedData: CachedData<T> = {
    data,
    timestamp: now,
    expiresAt: now + ttlMs
  };
  const serialised = JSON.stringify(cachedData);

  if (serialised.length > MAX_CACHE_ENTRY_BYTES) {
    console.warn(
      `Cache entry "${key}" (${serialised.length} bytes) exceeds ${MAX_CACHE_ENTRY_BYTES} byte limit; skipping cache write`
    );
    return;
  }

  try {
    localStorage.setItem(key, serialised);
  } catch (error) {
    if (isQuotaExceeded(error)) {
      try {
        clearAllCache();
        localStorage.setItem(key, serialised);
        return;
      } catch (retryError) {
        console.warn('Cache write still failing after reset; continuing without caching:', retryError);
        return;
      }
    }
    console.error('Error writing cache:', error);
  }
}

function isQuotaExceeded(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  // Different browsers surface this slightly differently; match on all known signals.
  return (
    error.name === 'QuotaExceededError' ||
    error.name === 'NS_ERROR_DOM_QUOTA_REACHED' ||
    /quota/i.test(error.message)
  );
}

/**
 * Clear cached data for a specific key
 */
export function clearCache(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch (error) {
    console.error('Error clearing cache:', error);
  }
}

/**
 * Clear every cache_* entry whose key starts with the given prefix.
 * Useful for invalidating a family of related caches at once
 * (e.g. all `cache_perf_aggregates_*` after a bet settles).
 */
export function clearCacheByPrefix(prefix: string): void {
  try {
    const keys = Object.keys(localStorage);
    keys.forEach(key => {
      if (key.startsWith(prefix)) {
        localStorage.removeItem(key);
      }
    });
  } catch (error) {
    console.error('Error clearing cache by prefix:', error);
  }
}

/**
 * Clear all cache entries (useful for debugging)
 */
export function clearAllCache(): void {
  try {
    // Only clear cache keys, not other localStorage items
    const keys = Object.keys(localStorage);
    keys.forEach(key => {
      if (key.startsWith('cache_')) {
        localStorage.removeItem(key);
      }
    });
  } catch (error) {
    console.error('Error clearing all cache:', error);
  }
}

/**
 * Check if cache exists and is valid
 */
export function isCacheValid(key: string): boolean {
  const cached = getCachedData(key);
  return cached !== null;
}

// One-shot cleanup: older builds cached oversized payloads that exceeded
// localStorage quota. Safe to remove these blocks a few releases after 2026-06.
try {
  localStorage.removeItem('cache_completed_runners');
  for (const key of Object.keys(localStorage)) {
    if (
      (key.startsWith('cache_todays_runners_') && !key.startsWith('cache_todays_runners_v2_')) ||
      (key.startsWith('cache_todays_meetings_') && !key.startsWith('cache_todays_meetings_v2_'))
    ) {
      localStorage.removeItem(key);
    }
  }
} catch {
  // ignore - storage may be unavailable
}

